import React, { useState, useRef, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated, Easing, TextInput, ScrollView, Modal } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from '../components/Gradients';
import GradientButton from '../components/GradientButton';
import Dropdown from '../components/Dropdown';
import AutocompleteDropdown from '../components/AutocompleteDropdown';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useAuth } from '../context/AuthContext';
import { theme } from '../constants/theme';
import Constants from 'expo-constants';
import { queryWithCacheFallback, queryCachedSGPs } from '../services/cacheService';

// Comprehensive search interface for both SGP and Straight betting
const UnifiedSearchScreen = () => {
    const { goBack, navigateTo } = useNavigation();
    const { userProfile } = useAuth();
    const { isTickerEnabled, totalHeaderHeight } = useTicker();
    
    // Helper: Convert SGP cache results to display format
    const convertSGPCacheToResults = (sgpCacheData) => {
        if (!sgpCacheData?.sgps || sgpCacheData.sgps.length === 0) return [];
        
        // Group by fixture
        const fixtureMap = {};
        for (const sgp of sgpCacheData.sgps) {
            const fixtureId = sgp.fixture_id;
            if (!fixtureMap[fixtureId]) {
                fixtureMap[fixtureId] = {
                    id: fixtureId,
                    homeTeam: sgp.home_team,
                    awayTeam: sgp.away_team,
                    startDate: sgp.start_date,
                    league: sgp.league,
                    sport: sgp.sport,
                    sgps: []
                };
            }
            fixtureMap[fixtureId].sgps.push(sgp);
        }
        
        // Convert to results array
        return Object.values(fixtureMap).map(fixture => ({
            type: 'sgp',
            fixture: fixture,
            sgps: fixture.sgps,
            _fromSGPCache: true
        }));
    };
    
    // Get active sportsbook filters from user preferences
    const activeFilters = userProfile?.preferences?.selectedSportsbooks || [];
    const unitSize = userProfile?.preferences?.unitSize || 10;
    const minBooksStraights = userProfile?.preferences?.minBooksStraights || 3;
    const minBooksSGP = userProfile?.preferences?.minBooksSGP || 4;
    
    // Core search state
    const [betType, setBetType] = useState('straight'); // 'straight', 'sgp', 'sgpx', 'teaser', 'round_robin'
    const [useSGPCache, setUseSGPCache] = useState(true); // Whether to check SGP cache first
    const [sport, setSport] = useState('all');
    const [league, setLeague] = useState('all');
    const [market, setMarket] = useState('all');
    const [book, setBook] = useState('all');
    const [team, setTeam] = useState('all');
    const [startTime, setStartTime] = useState('next_24h');
    const [oddsRange, setOddsRange] = useState({ min: '', max: '' });
    const [bookCount, setBookCount] = useState({ min: 1, max: 20 });
    const [boostPercent, setBoostPercent] = useState('0'); // Boost percentage for profit boost promos
    const [sharpBook, setSharpBook] = useState('all');
    const [future, setFuture] = useState(false);
    const [live, setLive] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSportsbooks, setSelectedSportsbooks] = useState([]); // Multi-select sportsbooks
    const [showSportsbooksDropdown, setShowSportsbooksDropdown] = useState(false); // Dropdown visibility
    
    // Advanced filters
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [homeTeams, setHomeTeams] = useState([]);
    const [awayTeams, setAwayTeams] = useState([]);
    const [conference, setConference] = useState('all');
    const [division, setDivision] = useState('all');
    const [players, setPlayers] = useState([]);
    const [position, setPosition] = useState('all');
    const [experience, setExperience] = useState('all');
    const [spreadRange, setSpreadRange] = useState({ min: '', max: '' });
    const [totalRange, setTotalRange] = useState({ min: '', max: '' });
    const [evRange, setEvRange] = useState({ min: '', max: '' });
    const [kellyCriterion, setKellyCriterion] = useState('0.25');
    
    // Results state
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cacheStatus, setCacheStatus] = useState(null); // Track cache status for user feedback
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [selectedBets, setSelectedBets] = useState([]); // Track selected bets for SGP
    
    // Smart dropdown data based on sport selection
    // Using sport-specific markets to avoid showing irrelevant options (e.g., no rushing for basketball)
    const sportData = {
        'all': { 
            leagues: ['NBA', 'NFL', 'MLB', 'NHL', 'NCAAF', 'NCAAB', 'MLS', 'Premier League'], 
            markets: ['Moneyline', 'Spread', 'Total', 'Player Props'], 
            positions: [] 
        },
        'basketball': {
            leagues: ['NBA', 'WNBA', 'NCAAB', 'G League', 'EuroLeague'],
            markets: [
                // Game markets
                'Moneyline', 'Point Spread', 'Total Points', '1st Half Moneyline', '1st Half Spread', 
                '1st Half Total', '1st Quarter Moneyline', '1st Quarter Spread', '1st Quarter Total', 
                'Team Total', 'Alternate Spread', 'Alternate Total',
                // Player markets (basketball-specific)
                'Player Points', 'Player Rebounds', 'Player Assists', 'Player Points + Rebounds', 
                'Player Points + Assists', 'Player Rebounds + Assists', 'Player Points + Rebounds + Assists', 
                'Player Steals', 'Player Blocks', 'Player Made Threes', 'Player Double Double', 
                'Player Triple Double', 'Player Turnovers'
            ],
            positions: ['PG', 'SG', 'SF', 'PF', 'C']
        },
        'football': {
            leagues: ['NFL', 'NCAAF', 'CFL', 'XFL'],
            markets: [
                // Game markets
                'Moneyline', 'Point Spread', 'Total Points', '1st Half Moneyline', '1st Half Spread',
                '1st Half Total', '1st Quarter Spread', '1st Quarter Total', 'Team Total', 
                'Alternate Spread', 'Alternate Total',
                // Player markets (football-specific)
                'Player Passing Yards', 'Player Passing TDs', 'Player Rushing Yards', 'Player Rushing TDs', 
                'Player Receiving Yards', 'Player Receptions', 'Anytime Touchdown', 'First Touchdown',
                'Player Passing Attempts', 'Player Completions', 'Player Interceptions'
            ],
            positions: ['QB', 'RB', 'WR', 'TE', 'K', 'DEF']
        },
        'baseball': {
            leagues: ['MLB'],
            markets: [
                // Game markets
                'Moneyline', 'Run Line', 'Total Runs', '1st 5 Innings Moneyline', '1st 5 Innings Run Line', 
                '1st 5 Innings Total', '1st Inning Result', 'Team Total', 'Alternate Run Line', 'Alternate Total',
                // Player markets (baseball-specific)
                'Player Hits', 'Player Home Runs', 'Player RBIs', 'Player Total Bases', 'Player Runs Scored',
                'Pitcher Strikeouts', 'Player Stolen Bases', 'Player Walks'
            ],
            positions: ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH']
        },
        'hockey': {
            leagues: ['NHL'],
            markets: [
                // Game markets
                'Moneyline', 'Puck Line', 'Total Goals', '1st Period Moneyline', '1st Period Puck Line', 
                '1st Period Total', 'Team Total', 'Regulation Time Result', '60 Minute Line',
                // Player markets (hockey-specific)
                'Player Goals', 'Player Assists', 'Player Points', 'Player Shots on Goal', 'Player Saves',
                'Anytime Goalscorer', 'First Goalscorer', 'Player Blocked Shots'
            ],
            positions: ['C', 'LW', 'RW', 'D', 'G']
        },
        'soccer': {
            leagues: ['MLS', 'Premier League', 'La Liga', 'Serie A', 'Bundesliga', 'Ligue 1', 'Champions League'],
            markets: [
                // Game markets
                'Moneyline', 'Draw No Bet', 'Double Chance', 'Total Goals', 'Both Teams to Score', 
                'Asian Handicap', 'Correct Score', '1st Half Result', '1st Half Total',
                // Player markets (soccer-specific)
                'Player Goals', 'Player Assists', 'Player Shots', 'Anytime Goalscorer', 'First Goalscorer'
            ],
            positions: ['GK', 'DEF', 'MID', 'FWD']
        },
        'tennis': {
            leagues: ['ATP', 'WTA', 'Grand Slams', 'Masters 1000', 'Challenger'],
            markets: [
                // Game markets
                'Moneyline', 'Set Spread', 'Total Sets', 'Total Games', '1st Set Winner', '1st Set Total Games',
                // Player markets (tennis-specific)
                'Player Aces', 'Player Double Faults'
            ],
            positions: []
        },
        'golf': {
            leagues: ['PGA Tour', 'European Tour', 'LPGA', 'Champions Tour', 'Majors'],
            markets: [
                'Tournament Winner', 'Top 5', 'Top 10', 'Top 20', 'Make Cut', 'First Round Leader', 'Head to Head'
            ],
            positions: []
        }
    };
    
    // Get filtered options based on sport
    const getFilteredOptions = (sportKey) => {
        const sportInfo = sportData[sportKey] || sportData['all'];
        return {
            leagues: sportInfo.leagues,
            markets: sportInfo.markets,
            positions: sportInfo.positions
        };
    };
    
    // Comprehensive sportsbooks list organized by popularity
    const ALL_SPORTSBOOKS = [
        // Tier 1: Most Popular (Major US Sportsbooks)
        { name: 'DraftKings', tier: 1, popular: true },
        { name: 'FanDuel', tier: 1, popular: true },
        { name: 'BetMGM', tier: 1, popular: true },
        { name: 'Caesars', tier: 1, popular: true },
        { name: 'ESPN Bet', tier: 1, popular: true },
        { name: 'Fanatics', tier: 1, popular: true },
        { name: 'Bet365', tier: 1, popular: true },
        // Tier 2: Well-Known
        { name: 'BetRivers', tier: 2, popular: false },
        { name: 'PointsBet', tier: 2, popular: false },
        { name: 'Hard Rock Bet', tier: 2, popular: false },
        { name: 'WynnBET', tier: 2, popular: false },
        { name: 'Unibet', tier: 2, popular: false },
        { name: 'Betfred', tier: 2, popular: false },
        { name: 'SuperBook', tier: 2, popular: false },
        { name: 'Tipico', tier: 2, popular: false },
        // Tier 3: Offshore / Specialty
        { name: 'Bovada', tier: 3, popular: false },
        { name: 'BetOnline', tier: 3, popular: false },
        { name: 'MyBookie', tier: 3, popular: false },
        { name: 'BetUS', tier: 3, popular: false },
        { name: 'Pinnacle', tier: 3, popular: false },
        { name: 'Bookmaker', tier: 3, popular: false },
        { name: 'Heritage Sports', tier: 3, popular: false },
        { name: 'BetAnySports', tier: 3, popular: false },
        // Tier 4: Regional / Newer
        { name: 'Borgata', tier: 4, popular: false },
        { name: 'BetPARX', tier: 4, popular: false },
        { name: 'Golden Nugget', tier: 4, popular: false },
        { name: 'Bally Bet', tier: 4, popular: false },
        { name: 'Circa Sports', tier: 4, popular: false },
        { name: 'Resorts World Bet', tier: 4, popular: false },
        { name: 'STN Sports', tier: 4, popular: false },
        // Tier 5: Alternative / Crypto / New Entrants
        { name: 'Novig', tier: 5, popular: false },
        { name: 'Underdog', tier: 5, popular: false },
        { name: 'Sleeper', tier: 5, popular: false },
        { name: 'Prophet X', tier: 5, popular: false },
        { name: 'Kalshi', tier: 5, popular: false },
        { name: 'Fliff', tier: 5, popular: false },
        { name: 'Rebet', tier: 5, popular: false },
        { name: 'Betr', tier: 5, popular: false },
        { name: 'Betway', tier: 5, popular: false },
    ];
    
    // Boost percentage options (5% increments)
    const BOOST_OPTIONS = [
        { value: '0', label: 'No Boost' },
        { value: '5', label: '5%' },
        { value: '10', label: '10%' },
        { value: '15', label: '15%' },
        { value: '20', label: '20%' },
        { value: '25', label: '25%' },
        { value: '30', label: '30%' },
        { value: '35', label: '35%' },
        { value: '40', label: '40%' },
        { value: '45', label: '45%' },
        { value: '50', label: '50%' },
        { value: '75', label: '75%' },
        { value: '100', label: '100%' },
    ];
    
    // Toggle sportsbook selection
    const toggleSportsbook = (bookName) => {
        setSelectedSportsbooks(prev => {
            if (prev.includes(bookName)) {
                return prev.filter(b => b !== bookName);
            } else {
                return [...prev, bookName];
            }
        });
    };
    
    // Select all popular sportsbooks
    const selectPopularSportsbooks = () => {
        const popular = ALL_SPORTSBOOKS.filter(b => b.popular).map(b => b.name);
        setSelectedSportsbooks(popular);
    };
    
    // Clear all sportsbook selections
    const clearSportsbookSelections = () => {
        setSelectedSportsbooks([]);
    };
    
    // Time range options
    const timeRanges = [
        { value: 'next_24h', label: 'Next 24 Hours' },
        { value: 'next_48h', label: 'Next 48 Hours' },
        { value: 'next_week', label: 'Next Week' },
        { value: 'today', label: 'Today Only' },
        { value: 'tomorrow', label: 'Tomorrow Only' },
        { value: 'this_weekend', label: 'This Weekend' },
        { value: 'custom', label: 'Custom Range' }
    ];
    
    // Odds range presets
    const oddsPresets = [
        { value: 'favorites', label: 'Favorites (-200 to -110)' },
        { value: 'even', label: 'Even Money (-110 to +110)' },
        { value: 'underdogs', label: 'Underdogs (+110 to +500)' },
        { value: 'longshots', label: 'Longshots (+500+)' },
        { value: 'custom', label: 'Custom Range' }
    ];
    
    // Search execution
    const executeSearch = async () => {
        setLoading(true);
        setCurrentPage(1);
        setCacheStatus(null);
        
        const searchStartTime = Date.now();
        const searchLogs = [];
        
        // Build search parameters
        const searchParams = {
            betType,
            sport: sport !== 'all' ? sport : null,
            league: league !== 'all' ? league : null,
            market: market !== 'all' ? market : null,
            book: book !== 'all' ? book : null,
            team: team !== 'all' ? team : null,
            startTime,
            oddsRange,
            bookCount,
            boostPercent,
            sharpBook: sharpBook !== 'all' ? sharpBook : null,
            future,
            live,
            query: searchQuery,
            // Advanced filters
            homeTeams: homeTeams.length > 0 ? homeTeams : null,
            awayTeams: awayTeams.length > 0 ? awayTeams : null,
            conference: conference !== 'all' ? conference : null,
            division: division !== 'all' ? division : null,
            players: players.length > 0 ? players : null,
            position: position !== 'all' ? position : null,
            experience: experience !== 'all' ? experience : null,
            spreadRange,
            totalRange,
            evRange,
            kellyCriterion
        };
        
        searchLogs.push({ timestamp: new Date().toISOString(), event: 'search_started', params: searchParams });
        
        try {
            // Build search prompt from parameters
            const promptParts = [];
            
            // Add sport/league
            if (sport !== 'all') {
                promptParts.push(sport);
            }
            if (league !== 'all') {
                promptParts.push(league.toUpperCase());
            }
            
            // Add market
            if (market !== 'all') {
                promptParts.push(market.replace(/_/g, ' '));
            }
            
            // Add search query
            if (searchQuery.trim()) {
                promptParts.push(searchQuery.trim());
            }
            
            // Add time filter
            if (startTime && startTime !== 'all') {
                if (startTime === 'next_24h') promptParts.push('today');
                else if (startTime === 'next_48h') promptParts.push('next 2 days');
                else if (startTime === 'next_week') promptParts.push('this week');
            }
            
            // Add future/live filters
            if (future) promptParts.push('futures');
            if (live) promptParts.push('live');
            
            const prompt = promptParts.length > 0 ? promptParts.join(' ') : 'best bets today';
            
            console.log('[UnifiedSearch] Starting cache-first search with prompt:', prompt);
            
            // Use selected sportsbooks from multi-select dropdown if any, otherwise fall back to single book selection
            const effectiveSportsbooks = selectedSportsbooks.length > 0 
                ? selectedSportsbooks.join(',') 
                : (book !== 'all' ? book : undefined);
            
            // ⚡ CHECK SGP CACHE FIRST if bet type is SGP
            let sgpCacheResult = null;
            if ((betType === 'sgp' || betType === 'sgpx') && useSGPCache) {
                console.log('[UnifiedSearch] 🎯 Checking SGP cache first...');
                setCacheStatus({ stage: 'checking_sgp_cache', message: 'Checking pre-generated SGPs...' });
                
                try {
                    const { queryCachedSGPs } = await import('../services/cacheService');
                    sgpCacheResult = await queryCachedSGPs({
                        league: league !== 'all' ? league : undefined,
                        sport: sport !== 'all' ? sport : undefined,
                        team: team !== 'all' ? team : undefined,
                        limit: 50,
                        max_age_hours: 6,
                        sort_by: 'ev'
                    });
                    
                    console.log('[UnifiedSearch] SGP cache result:', {
                        success: sgpCacheResult?.success,
                        sgpsCount: sgpCacheResult?.sgps?.length || 0,
                        fixturesCount: sgpCacheResult?.fixtures?.length || 0
                    });
                    
                    if (sgpCacheResult?.success && sgpCacheResult?.sgps?.length > 0) {
                        console.log('[UnifiedSearch] ✅ Using SGPs from cache!');
                        setCacheStatus({ stage: 'sgp_cache_hit', message: `Loaded ${sgpCacheResult.sgps.length} pre-generated SGPs` });
                        
                        // Convert SGP cache to results format
                        const resultsFromCache = convertSGPCacheToResults(sgpCacheResult);
                        setResults(resultsFromCache);
                        setTotalResults(sgpCacheResult.sgps.length);
                        setTotalPages(Math.ceil(sgpCacheResult.sgps.length / 20));
                        setLoading(false);
                        return; // Exit early - we have our results!
                    } else {
                        console.log('[UnifiedSearch] ℹ️ No SGPs in cache, falling back to live generation');
                        setCacheStatus({ stage: 'sgp_cache_miss', message: 'No cached SGPs - generating live...' });
                    }
                } catch (sgpCacheError) {
                    console.warn('[UnifiedSearch] Error checking SGP cache:', sgpCacheError);
                    setCacheStatus({ stage: 'sgp_cache_error', message: 'SGP cache error - using live generation' });
                }
            }
            
            // ⚡ Use cache-first approach
            const queryResult = await queryWithCacheFallback(
                prompt,
                {
                    league: league !== 'all' ? league : undefined,
                    sport: sport !== 'all' ? sport : undefined,
                    team: team !== 'all' ? team : undefined,
                    market: market !== 'all' ? market : undefined,
                prefer: betType === 'straight' ? 'straight' : 'sgp',
                defaults: {
                    sport: sport !== 'all' ? sport : undefined,
                        league: league !== 'all' ? league?.toLowerCase() : undefined,
                    sportsbooks: effectiveSportsbooks,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    now: new Date().toISOString()
                }
                },
                (status) => {
                    // Update cache status for user feedback
                    setCacheStatus(status);
                    console.log('[UnifiedSearch] Cache status:', status);
                }
            );
            
            searchLogs.push({ 
                timestamp: new Date().toISOString(), 
                event: 'query_complete',
                cached: queryResult.cached 
            });
            
            if (!queryResult.success) {
                console.error('[UnifiedSearch] Query failed');
                setResults([]);
                setTotalResults(0);
                setTotalPages(0);
                setCacheStatus({ stage: 'error', message: 'Search failed' });
                return;
            }
            
            // Parse results (format depends on cache vs live)
            let bodyJson = queryResult;
            
            // If we got odds directly from cache, wrap them in the expected format
            if (queryResult.odds) {
                bodyJson = {
                    results: [{
                        items: queryResult.odds
                    }]
                };
            }
            
            const arr = Array.isArray(bodyJson?.results) ? bodyJson.results : [];
            const items = arr.flatMap(r => Array.isArray(r?.items) ? r.items : [])
                .flatMap(x => Array.isArray(x?.odds) ? x.odds : [x]);
            
            searchLogs.push({ 
                timestamp: new Date().toISOString(), 
                event: 'results_parsed', 
                resultsCount: arr.length, 
                itemsCount: items.length,
                endpoints: bodyJson?.endpoints || []
            });
            
            // Transform backend results to table format
            const transformedResults = items
                .filter(o => o && (o.market || o.market_id) && (o.selection || o.name))
                .slice(0, 100)
                .map((o, idx) => {
                    const price = o.price ?? o.american_price ?? null;
                    const aiProb = o.ai_prob ?? null;
                    let implied = null;
                    let ev = null;
                    
                    if (typeof price === 'number' && !isNaN(price)) {
                        implied = price > 0 ? (100 / (price + 100)) : ((-price) / ((-price) + 100));
                    }
                    
                    if (aiProb !== null && implied !== null) {
                        ev = (aiProb - implied) * 100;
                    }
                    
                    // Calculate fair value (convert AI prob to American odds)
                    let fairValue = null;
                    if (aiProb !== null && aiProb > 0 && aiProb < 1) {
                        fairValue = aiProb >= 0.5 
                            ? Math.round(-100 * aiProb / (1 - aiProb))
                            : Math.round(100 * (1 - aiProb) / aiProb);
                    }
                    
                    // Calculate Kelly
                    const kelly = (ev !== null && ev > 0 && aiProb !== null && implied !== null) 
                        ? ((aiProb * (price > 0 ? (price / 100) : (100 / -price)) - (1 - aiProb)) / (price > 0 ? (price / 100) : (100 / -price))) * 100
                        : 0;
                    
                    // ⭐ FIX: Get sportsbook count from bookOdds (the correct source)
                    // bookOdds contains ALL sportsbooks for this bet line
                    const bookOdds = o.bookOdds || {};
                    const bookCount = Object.keys(bookOdds).length || (o.sportsbook_count || o.book_count || 1);
                    const allBooks = Object.keys(bookOdds);
                    
                    // 🐛 DEBUG: Log bookOdds for first few items to verify data
                    if (idx < 3) {
                        console.log(`[SearchResults] Item ${idx}: ${o.selection || o.name}`, {
                            bookOddsKeys: allBooks.length,
                            bookOddsData: bookOdds,
                            sportsbook_count: o.sportsbook_count,
                            book_count: o.book_count,
                            primaryBook: o.sportsbook
                        });
                    }
                    
                    // ⭐ FIX: Find best odds across all sportsbooks for display
                    let bestPrice = price;
                    let bestBook = o.sportsbook || 'N/A';
                    if (allBooks.length > 0) {
                        // Find the best price (highest for positive odds, closest to 0 for negative)
                        allBooks.forEach(book => {
                            const bookData = bookOdds[book];
                            const bookPrice = typeof bookData === 'object' ? bookData.price : bookData;
                            if (bookPrice != null) {
                                // For favorites (negative odds): less negative is better (-110 > -120)
                                // For underdogs (positive odds): higher is better (+150 > +130)
                                if (bestPrice == null || 
                                    (bookPrice > 0 && bestPrice > 0 && bookPrice > bestPrice) ||
                                    (bookPrice < 0 && bestPrice < 0 && bookPrice > bestPrice) ||
                                    (bookPrice > 0 && bestPrice < 0)) {
                                    bestPrice = bookPrice;
                                    bestBook = book;
                                }
                            }
                        });
                    }
                    
                    return {
                        id: o.id || `${idx}`,
                        league: (o.league || o.sport || '').toUpperCase(),
                        date: o.commence_time ? new Date(o.commence_time).toLocaleDateString() : 'TBD',
                        time: o.commence_time ? new Date(o.commence_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'TBD',
                        game: o.fixture || o.game || `${o.home_team || ''} vs ${o.away_team || ''}` || 'N/A',
                        market: o.market || o.market_id || 'N/A',
                        bet: `${o.selection || o.name}${o.points || o.selection_line ? ` ${o.points || o.selection_line}` : ''}`,
                        book: bestBook, // ⭐ Show best book
                        odds: bestPrice !== null ? (bestPrice > 0 ? `+${bestPrice}` : `${bestPrice}`) : 'N/A',
                        booksCalculated: bookCount, // ⭐ FIX: Correctly count from bookOdds
                        allBooks: allBooks, // ⭐ NEW: All available sportsbooks
                        bookOdds: bookOdds, // ⭐ NEW: Full bookOdds for expansion
                        fairValue: fairValue !== null ? (fairValue > 0 ? `+${fairValue}` : `${fairValue}`) : 'N/A',
                        evPercent: ev !== null ? ev.toFixed(2) : '0.00',
                        kelly: Math.max(0, Math.min(25, kelly)).toFixed(1),
                        raw: o // Store raw data for later use
                    };
                });
            
            // Apply additional filters
            let filteredResults = transformedResults;
            
            // Filter by book if specified
            if (book !== 'all') {
                filteredResults = filteredResults.filter(r => 
                    r.book.toLowerCase().includes(book.toLowerCase())
                );
            }
            
            // Filter by EV range
            if (evRange.min !== '' || evRange.max !== '') {
                filteredResults = filteredResults.filter(r => {
                    const ev = parseFloat(r.evPercent);
                    if (evRange.min !== '' && ev < parseFloat(evRange.min)) return false;
                    if (evRange.max !== '' && ev > parseFloat(evRange.max)) return false;
                    return true;
                });
            }
            
            // Apply Kelly Criterion to calculate suggested bet size
            const kellyFraction = parseFloat(kellyCriterion) || 0.25;
            filteredResults = filteredResults.map(r => {
                const ev = parseFloat(r.evPercent);
                const aiProb = parseFloat(r.kelly) / 100; // Convert kelly% back to probability
                return { ...r, kellyCriterion: kellyFraction, suggestedKelly: parseFloat(r.kelly) };
            });
            
            // Sort by EV% descending
            filteredResults.sort((a, b) => parseFloat(b.evPercent) - parseFloat(a.evPercent));
            
            searchLogs.push({ 
                timestamp: new Date().toISOString(), 
                event: 'search_completed', 
                finalResultsCount: filteredResults.length 
            });
            
            setResults(filteredResults);
            setTotalResults(filteredResults.length);
            setTotalPages(Math.ceil(filteredResults.length / 10));
            
            // Save search to metrics
            const searchDuration = Date.now() - searchStartTime;
            await saveSearchMetrics(prompt, searchParams, filteredResults.length, searchLogs, searchDuration);
            
        } catch (error) {
            console.error('Search error:', error);
            searchLogs.push({ timestamp: new Date().toISOString(), event: 'search_exception', error: error.message });
            setResults([]);
            setTotalResults(0);
            setTotalPages(0);
            
            // Save failed search to metrics
            await saveSearchMetrics(prompt || searchQuery, searchParams, 0, searchLogs, Date.now() - searchStartTime);
        } finally {
            setLoading(false);
        }
    };
    
    // Save search metrics to Firestore
    const saveSearchMetrics = async (query, params, resultsCount, logs, duration) => {
        try {
            const { db, auth } = require('../firebaseConfig');
            const { collection, addDoc, serverTimestamp } = require('firebase/firestore');
            
            await addDoc(collection(db, 'metrics_queries'), {
                prompt: query,
                params: params,
                resultsCount: resultsCount,
                logs: logs,
                durationMs: duration,
                uid: auth.currentUser?.uid || 'anonymous',
                createdAt: serverTimestamp(),
                broaden: false,
                feedback: null
            });
        } catch (error) {
            console.error('Failed to save search metrics:', error);
        }
    };
    
    // Generate mock results for testing
    const generateMockResults = (params) => {
        const mockData = [];
        const leagues = params.league ? [params.league] : ['NBA', 'NFL', 'MLB'];
        const books = params.book ? [params.book] : ['DraftKings', 'FanDuel', 'Caesars'];
        
        for (let i = 0; i < 25; i++) {
            const league = leagues[Math.floor(Math.random() * leagues.length)];
            const book = books[Math.floor(Math.random() * books.length)];
            const ev = (Math.random() - 0.4) * 10; // -4% to +6% EV
            
            mockData.push({
                id: i + 1,
                league,
                date: new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                time: `${Math.floor(Math.random() * 24)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`,
                game: `${getRandomTeam()} vs ${getRandomTeam()}`,
                market: getRandomMarket(league),
                bet: getRandomBet(league),
                book,
                odds: Math.random() > 0.5 ? `+${Math.floor(Math.random() * 500) + 100}` : `-${Math.floor(Math.random() * 200) + 110}`,
                booksCalculated: Math.floor(Math.random() * 8) + 3,
                fairValue: Math.random() > 0.5 ? `+${Math.floor(Math.random() * 500) + 100}` : `-${Math.floor(Math.random() * 200) + 110}`,
                evPercent: ev.toFixed(2),
                kelly: Math.max(0, Math.min(25, ev * 2)).toFixed(1)
            });
        }
        
        return mockData.sort((a, b) => parseFloat(b.evPercent) - parseFloat(a.evPercent));
    };
    
    const getRandomTeam = () => {
        const teams = ['Lakers', 'Warriors', 'Celtics', 'Heat', 'Nuggets', 'Bucks', 'Suns', 'Mavericks'];
        return teams[Math.floor(Math.random() * teams.length)];
    };
    
    const getRandomMarket = (league) => {
        const markets = {
            'NBA': ['Moneyline', 'Spread', 'Total', 'Player Points', 'Player Rebounds'],
            'NFL': ['Moneyline', 'Spread', 'Total', 'Player Passing Yards', 'Player Touchdowns'],
            'MLB': ['Moneyline', 'Run Line', 'Total', 'Player Hits', 'Player RBIs']
        };
        const leagueMarkets = markets[league] || ['Moneyline', 'Spread', 'Total'];
        return leagueMarkets[Math.floor(Math.random() * leagueMarkets.length)];
    };
    
    const getRandomBet = (league) => {
        const bets = {
            'NBA': ['Over 25.5 Points', 'Under 25.5 Points', 'Lakers -3.5', 'Over 220.5 Total'],
            'NFL': ['Over 250 Passing Yards', 'Under 250 Passing Yards', 'Patriots -7', 'Over 45.5 Total'],
            'MLB': ['Over 8.5 Total', 'Under 8.5 Total', 'Yankees -1.5', 'Over 0.5 Home Runs']
        };
        const leagueBets = bets[league] || ['Over/Under', 'Spread', 'Moneyline'];
        return leagueBets[Math.floor(Math.random() * leagueBets.length)];
    };
    
    // Clear all filters
    const clearAllFilters = () => {
        setBetType('straight');
        setSport('all');
        setLeague('all');
        setMarket('all');
        setBook('all');
        setTeam('all');
        setStartTime('next_24h');
        setOddsRange({ min: '', max: '' });
        setBookCount({ min: 1, max: 20 });
        setBoostPercent('0');
        setSharpBook('all');
        setFuture(false);
        setLive(false);
        setSearchQuery('');
        setHomeTeams([]);
        setAwayTeams([]);
        setConference('all');
        setDivision('all');
        setPlayers([]);
        setPosition('all');
        setExperience('all');
        setSpreadRange({ min: '', max: '' });
        setTotalRange({ min: '', max: '' });
        setEvRange({ min: '', max: '' });
        setKellyCriterion('0.25');
        setSelectedSportsbooks([]);
    };
    
    // Get current filtered options
    const currentOptions = getFilteredOptions(sport);
    
    // Handle bet selection/deselection
    const toggleBetSelection = (result) => {
        setSelectedBets(prev => {
            const isSelected = prev.some(bet => bet.id === result.id);
            if (isSelected) {
                return prev.filter(bet => bet.id !== result.id);
            } else {
                return [...prev, result];
            }
        });
    };
    
    // Send selected bets to SGP builder
    const sendToSGPBuilder = () => {
        if (selectedBets.length === 0) return;
        
        // Store selected bets in session storage for SGP builder to pick up
        if (Platform.OS === 'web') {
            try {
                window.sessionStorage.setItem('sgp:selectedBets', JSON.stringify(selectedBets));
            } catch (e) {
                console.error('Failed to save selected bets:', e);
            }
        }
        
        // Navigate to SGP builder
        navigateTo('SgpFlow');
    };
    
    return (
        <View style={styles.container}>
            <NavigationHeader />
            
            <ScrollView style={[styles.content, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + 20) : 20 
            }]} showsVerticalScrollIndicator={false}>
                {/* Main Search Interface */}
                <View style={styles.searchSection}>
                    <Text style={styles.sectionTitle}>Betting Search</Text>
                    
                    {/* Primary Controls Row 1 */}
                    <View style={styles.controlRow}>
                        <View style={styles.controlGroup}>
                            <Text style={styles.controlLabel}>Bet Type</Text>
                            <Dropdown
                                options={[
                                    { value: 'straight', label: 'Straight' },
                                    { value: 'sgp', label: 'SGP' },
                                    { value: 'sgpx', label: 'SGPx' },
                                    { value: 'teaser', label: 'Teaser' },
                                    { value: 'round_robin', label: 'Round Robin' }
                                ]}
                                value={betType}
                                onValueChange={setBetType}
                                placeholder="Select bet type"
                            />
                        </View>
                        
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="Sport"
                                options={['All Sports', 'Basketball', 'Football', 'Baseball', 'Hockey', 'Soccer', 'Tennis', 'Golf']}
                                value={sport === 'all' ? 'All Sports' : sport.charAt(0).toUpperCase() + sport.slice(1)}
                                onValueChange={(value) => {
                                    const sportValue = value === 'All Sports' ? 'all' : value.toLowerCase();
                                    setSport(sportValue);
                                    setLeague('all'); // Reset league when sport changes
                                    setMarket('all'); // Reset market when sport changes
                                }}
                                placeholder="Type or select sport..."
                            />
                        </View>
                        
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="League"
                                options={['All Leagues', ...currentOptions.leagues]}
                                value={league === 'all' ? 'All Leagues' : league.toUpperCase()}
                                onValueChange={(value) => {
                                    setLeague(value === 'All Leagues' ? 'all' : value.toLowerCase());
                                }}
                                placeholder="Type or select league..."
                            />
                        </View>
                    </View>
                    
                    {/* Primary Controls Row 2 */}
                    <View style={styles.controlRow}>
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="Market"
                                options={['All Markets', ...currentOptions.markets]}
                                value={market === 'all' ? 'All Markets' : market.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                onValueChange={(value) => {
                                    setMarket(value === 'All Markets' ? 'all' : value.toLowerCase().replace(/\s+/g, '_'));
                                }}
                                placeholder="Type or select market..."
                            />
                        </View>
                        
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="Book"
                                options={['All Books', ...keySportsbooks]}
                                value={book === 'all' ? 'All Books' : book.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                onValueChange={(value) => {
                                    setBook(value === 'All Books' ? 'all' : value.toLowerCase().replace(/\s+/g, '_'));
                                }}
                                placeholder="Type or select book..."
                            />
                        </View>
                        
                        <View style={styles.controlGroup}>
                            <Text style={styles.controlLabel}>Start Time</Text>
                            <Dropdown
                                options={timeRanges}
                                value={startTime}
                                onValueChange={setStartTime}
                                placeholder="Select time"
                            />
                        </View>
                    </View>
                    
                    {/* Primary Controls Row 3: Boost and Sportsbooks */}
                    <View style={styles.controlRow}>
                        <View style={[styles.controlGroup, { flex: 0.5 }]}>
                            <Text style={styles.controlLabel}>Profit Boost %</Text>
                            <Dropdown
                                options={BOOST_OPTIONS}
                                value={boostPercent}
                                onValueChange={setBoostPercent}
                                placeholder="Select boost"
                            />
                        </View>
                        
                        <View style={[styles.controlGroup, { flex: 1.5 }]}>
                            <Text style={styles.controlLabel}>Sportsbooks ({selectedSportsbooks.length} selected)</Text>
                            <TouchableOpacity 
                                style={styles.sportsbooksSelector}
                                onPress={() => setShowSportsbooksDropdown(!showSportsbooksDropdown)}
                            >
                                <Text style={styles.sportsbooksSelectorText} numberOfLines={1}>
                                    {selectedSportsbooks.length === 0 
                                        ? 'All Sportsbooks (click to filter)' 
                                        : selectedSportsbooks.slice(0, 3).join(', ') + (selectedSportsbooks.length > 3 ? ` +${selectedSportsbooks.length - 3} more` : '')
                                    }
                                </Text>
                                <Text style={styles.sportsbooksArrow}>{showSportsbooksDropdown ? '▲' : '▼'}</Text>
                            </TouchableOpacity>
                            
                            {showSportsbooksDropdown && (
                                <View style={styles.sportsbooksDropdown}>
                                    <View style={styles.sportsbooksDropdownHeader}>
                                        <TouchableOpacity onPress={selectPopularSportsbooks} style={styles.sportsbooksQuickAction}>
                                            <Text style={styles.sportsbooksQuickActionText}>Select Popular</Text>
                                        </TouchableOpacity>
                                        <TouchableOpacity onPress={clearSportsbookSelections} style={[styles.sportsbooksQuickAction, { backgroundColor: 'rgba(248,113,113,0.2)' }]}>
                                            <Text style={[styles.sportsbooksQuickActionText, { color: '#f87171' }]}>Clear All</Text>
                                        </TouchableOpacity>
                                        <TouchableOpacity onPress={() => setShowSportsbooksDropdown(false)} style={styles.sportsbooksCloseBtn}>
                                            <Text style={styles.sportsbooksCloseBtnText}>Done ✓</Text>
                                        </TouchableOpacity>
                                    </View>
                                    
                                    <View style={styles.sportsbooksList}>
                                        {/* Popular Sportsbooks Section */}
                                        <Text style={styles.sportsbooksTierLabel}>⭐ Most Popular</Text>
                                        <View style={styles.sportsbooksGrid}>
                                            {ALL_SPORTSBOOKS.filter(b => b.tier === 1).map((book) => (
                                                <TouchableOpacity
                                                    key={book.name}
                                                    style={[
                                                        styles.sportsbookChip,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                    ]}
                                                    onPress={() => toggleSportsbook(book.name)}
                                                >
                                                    <Text style={[
                                                        styles.sportsbookChipText,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                    ]}>
                                                        {book.name}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                        
                                        {/* Well-Known Section */}
                                        <Text style={styles.sportsbooksTierLabel}>Well-Known</Text>
                                        <View style={styles.sportsbooksGrid}>
                                            {ALL_SPORTSBOOKS.filter(b => b.tier === 2).map((book) => (
                                                <TouchableOpacity
                                                    key={book.name}
                                                    style={[
                                                        styles.sportsbookChip,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                    ]}
                                                    onPress={() => toggleSportsbook(book.name)}
                                                >
                                                    <Text style={[
                                                        styles.sportsbookChipText,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                    ]}>
                                                        {book.name}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                        
                                        {/* Offshore / Specialty Section */}
                                        <Text style={styles.sportsbooksTierLabel}>Offshore / Sharp</Text>
                                        <View style={styles.sportsbooksGrid}>
                                            {ALL_SPORTSBOOKS.filter(b => b.tier === 3).map((book) => (
                                                <TouchableOpacity
                                                    key={book.name}
                                                    style={[
                                                        styles.sportsbookChip,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                    ]}
                                                    onPress={() => toggleSportsbook(book.name)}
                                                >
                                                    <Text style={[
                                                        styles.sportsbookChipText,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                    ]}>
                                                        {book.name}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                        
                                        {/* Regional / Newer Section */}
                                        <Text style={styles.sportsbooksTierLabel}>Regional / Newer</Text>
                                        <View style={styles.sportsbooksGrid}>
                                            {ALL_SPORTSBOOKS.filter(b => b.tier === 4).map((book) => (
                                                <TouchableOpacity
                                                    key={book.name}
                                                    style={[
                                                        styles.sportsbookChip,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                    ]}
                                                    onPress={() => toggleSportsbook(book.name)}
                                                >
                                                    <Text style={[
                                                        styles.sportsbookChipText,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                    ]}>
                                                        {book.name}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                        
                                        {/* Alternative / New Entrants Section */}
                                        <Text style={styles.sportsbooksTierLabel}>Alternative / New</Text>
                                        <View style={styles.sportsbooksGrid}>
                                            {ALL_SPORTSBOOKS.filter(b => b.tier === 5).map((book) => (
                                                <TouchableOpacity
                                                    key={book.name}
                                                    style={[
                                                        styles.sportsbookChip,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                    ]}
                                                    onPress={() => toggleSportsbook(book.name)}
                                                >
                                                    <Text style={[
                                                        styles.sportsbookChipText,
                                                        selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                    ]}>
                                                        {book.name}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>
                                </View>
                            )}
                        </View>
                    </View>
                    
                    {/* Search Query */}
                    <View style={styles.searchQueryContainer}>
                        <Text style={styles.controlLabel}>Search Query</Text>
                        <TextInput
                            style={styles.searchInput}
                            value={searchQuery}
                            onChangeText={setSearchQuery}
                            placeholder="Enter search terms (e.g., 'Lakers vs Warriors tonight')"
                            placeholderTextColor="rgba(255,255,255,0.5)"
                        />
                    </View>
                    
                    {/* Advanced Filters Toggle */}
                    <TouchableOpacity 
                        style={styles.advancedToggle}
                        onPress={() => setShowAdvanced(!showAdvanced)}
                    >
                        <Text style={styles.advancedToggleText}>
                            {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
                        </Text>
                    </TouchableOpacity>
                    
                    {/* Advanced Filters Panel */}
                    {showAdvanced && (
                        <View style={styles.advancedPanel}>
                            <Text style={styles.advancedTitle}>Advanced Filters</Text>
                            
                            {/* Value Filters */}
                            <View style={styles.advancedSection}>
                                <Text style={styles.advancedSectionTitle}>Value Filters</Text>
                                <View style={styles.rangeRow}>
                                    <View style={styles.rangeGroup}>
                                        <Text style={styles.rangeLabel}>EV% Range</Text>
                                        <View style={styles.rangeInputs}>
                                            <TextInput
                                                style={styles.rangeInput}
                                                value={evRange.min}
                                                onChangeText={(text) => setEvRange(prev => ({ ...prev, min: text }))}
                                                placeholder="Min"
                                                placeholderTextColor="rgba(255,255,255,0.5)"
                                            />
                                            <Text style={styles.rangeSeparator}>to</Text>
                                            <TextInput
                                                style={styles.rangeInput}
                                                value={evRange.max}
                                                onChangeText={(text) => setEvRange(prev => ({ ...prev, max: text }))}
                                                placeholder="Max"
                                                placeholderTextColor="rgba(255,255,255,0.5)"
                                            />
                                        </View>
                                    </View>
                                    
                                    <View style={styles.rangeGroup}>
                                        <Text style={styles.rangeLabel}>Kelly Criterion (Fractional)</Text>
                                        <View style={styles.kellyButtonsRow}>
                                            {['0.125', '0.25', '0.5', '1.0'].map((val) => (
                                                <TouchableOpacity
                                                    key={val}
                                                    onPress={() => setKellyCriterion(val)}
                                                    style={[
                                                        styles.kellyPresetButton,
                                                        kellyCriterion === val && styles.kellyPresetButtonActive
                                                    ]}
                                                >
                                                    <Text style={[
                                                        styles.kellyPresetText,
                                                        kellyCriterion === val && styles.kellyPresetTextActive
                                                    ]}>
                                                        {val === '0.125' ? '1/8' : val === '0.25' ? '1/4' : val === '0.5' ? '1/2' : 'Full'}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                            <TextInput
                                                value={kellyCriterion}
                                                onChangeText={setKellyCriterion}
                                                placeholder="Custom"
                                                placeholderTextColor="rgba(255,255,255,0.4)"
                                                style={styles.kellyCustomInput}
                                            />
                                        </View>
                                        <Text style={styles.kellyHint}>
                                            1/4 Kelly (recommended) • 1/2 Kelly (moderate) • Full Kelly (aggressive)
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            
                            {/* Additional Filters */}
                            <View style={styles.advancedSection}>
                                <Text style={styles.advancedSectionTitle}>Additional Filters</Text>
                                <View style={styles.checkboxRow}>
                                    <TouchableOpacity 
                                        style={[styles.checkbox, future && styles.checkboxActive]}
                                        onPress={() => setFuture(!future)}
                                    >
                                        <Text style={[styles.checkboxText, future && styles.checkboxTextActive]}>
                                            Future Bets
                                        </Text>
                                    </TouchableOpacity>
                                    
                                    <TouchableOpacity 
                                        style={[styles.checkbox, live && styles.checkboxActive]}
                                        onPress={() => setLive(!live)}
                                    >
                                        <Text style={[styles.checkboxText, live && styles.checkboxTextActive]}>
                                            Live Events
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        </View>
                    )}
                    
                    {/* Selected Sportsbooks Summary (if any selected) */}
                    {selectedSportsbooks.length > 0 && (
                        <View style={styles.selectedSportsbooksSection}>
                            <View style={styles.selectedSportsbooksHeader}>
                                <Text style={styles.selectedSportsbooksLabel}>
                                    🎯 Filtering by {selectedSportsbooks.length} sportsbook{selectedSportsbooks.length !== 1 ? 's' : ''}:
                            </Text>
                                <TouchableOpacity onPress={clearSportsbookSelections}>
                                    <Text style={styles.clearSportsbooksBtn}>Clear</Text>
                                </TouchableOpacity>
                                    </View>
                            <View style={styles.selectedSportsbooksChips}>
                                {selectedSportsbooks.map((book, idx) => (
                                    <TouchableOpacity 
                                        key={idx} 
                                        style={styles.selectedSportsbookChip}
                                        onPress={() => toggleSportsbook(book)}
                                    >
                                        <Text style={styles.selectedSportsbookChipText}>{book}</Text>
                                        <Text style={styles.selectedSportsbookRemove}>×</Text>
                                    </TouchableOpacity>
                                ))}
                                    </View>
                        </View>
                    )}
                    
                    {/* Action Buttons */}
                    <View style={styles.actionButtons}>
                        <GradientButton
                            text="Search"
                            onPress={executeSearch}
                            style={styles.searchButton}
                            gradientColors={["#707070", "#FFFFFF"]}
                        />
                        <TouchableOpacity style={styles.clearButton} onPress={clearAllFilters}>
                            <Text style={styles.clearButtonText}>Clear Search Filters</Text>
                        </TouchableOpacity>
                    </View>
                    
                    {/* Cache Status Display */}
                    {loading && cacheStatus && (
                        <View style={[
                            styles.cacheStatusContainer,
                            cacheStatus.stage === 'cache_miss' && styles.cacheStatusHighlight
                        ]}>
                            <Text style={styles.cacheStatusText}>
                                {cacheStatus.stage === 'checking_cache' && '🔍 '}
                                {cacheStatus.stage === 'cache_hit' && '⚡ '}
                                {cacheStatus.stage === 'cache_miss' && '🔎 '}
                                {cacheStatus.stage === 'error' && '⚠️ '}
                                {cacheStatus.message}
                            </Text>
                        </View>
                    )}
                </View>
                
                {/* Results Section */}
                {results.length > 0 && (
                    <View style={styles.resultsSection}>
                        <View style={styles.resultsHeader}>
                            <Text style={styles.resultsTitle}>
                                Results ({totalResults} found)
                            </Text>
                            <Text style={styles.resultsSubtitle}>
                                Page {currentPage} of {totalPages}
                            </Text>
                        </View>
                        
                        {/* Results Table */}
                        <View style={styles.resultsTable}>
                            <View style={styles.tableHeader}>
                                <Text style={[styles.headerCell, styles.selectCell]}>Select</Text>
                                <Text style={styles.headerCell}>League</Text>
                                <Text style={styles.headerCell}>Date</Text>
                                <Text style={styles.headerCell}>Time</Text>
                                <Text style={styles.headerCell}>Game</Text>
                                <Text style={styles.headerCell}>Market</Text>
                                <Text style={styles.headerCell}>Bet</Text>
                                <Text style={styles.headerCell}>Book</Text>
                                <Text style={styles.headerCell}>Odds</Text>
                                <Text style={styles.headerCell}>Books</Text>
                                <Text style={styles.headerCell}>Fair Value</Text>
                                <Text style={styles.headerCell}>EV%</Text>
                                <Text style={styles.headerCell}>Kelly</Text>
                            </View>
                            
                            {results.slice((currentPage - 1) * 10, currentPage * 10).map((result, index) => {
                                const isSelected = selectedBets.some(bet => bet.id === result.id);
                                return (
                                    <View key={result.id} style={[styles.tableRow, index % 2 === 0 && styles.tableRowEven]}>
                                        <TouchableOpacity 
                                            style={[styles.tableCell, styles.selectCell]}
                                            onPress={() => toggleBetSelection(result)}
                                        >
                                            <View style={[styles.selectCheckbox, isSelected && styles.selectCheckboxActive]}>
                                                {isSelected && <Text style={styles.checkmark}>✓</Text>}
                                            </View>
                                        </TouchableOpacity>
                                        <Text style={styles.tableCell}>{result.league}</Text>
                                        <Text style={styles.tableCell}>{result.date}</Text>
                                        <Text style={styles.tableCell}>{result.time}</Text>
                                        <Text style={styles.tableCell}>{result.game}</Text>
                                        <Text style={styles.tableCell}>{result.market}</Text>
                                        <Text style={styles.tableCell}>{result.bet}</Text>
                                        <Text style={styles.tableCell} numberOfLines={1}>
                                            {result.book}
                                        </Text>
                                        <Text style={styles.tableCell}>{result.odds}</Text>
                                        <Text style={[
                                            styles.tableCell, 
                                            result.booksCalculated >= 10 && styles.goodBookCount,
                                            result.booksCalculated < 5 && styles.lowBookCount
                                        ]}>
                                            {result.booksCalculated}
                                        </Text>
                                        <Text style={styles.tableCell}>{result.fairValue}</Text>
                                        <Text style={[styles.tableCell, parseFloat(result.evPercent) >= 0 ? styles.positiveValue : styles.negativeValue]}>
                                            {result.evPercent}%
                                        </Text>
                                        <Text style={styles.tableCell}>{result.kelly}%</Text>
                                    </View>
                                );
                            })}
                        </View>
                        
                        {/* Send to SGP Builder Button */}
                        {selectedBets.length > 0 && (
                            <View style={styles.sgpBuilderSection}>
                                <GradientButton
                                    text={`Send ${selectedBets.length} Bet${selectedBets.length > 1 ? 's' : ''} to SGP Builder`}
                                    onPress={sendToSGPBuilder}
                                    style={styles.sgpBuilderButton}
                                    gradientColors={["#8b5cf6", "#c4b5fd"]}
                                />
                            </View>
                        )}
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <View style={styles.pagination}>
                                <TouchableOpacity 
                                    style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
                                    onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <Text style={styles.paginationButtonText}>Previous</Text>
                                </TouchableOpacity>
                                
                                <Text style={styles.paginationText}>
                                    {currentPage} of {totalPages}
                                </Text>
                                
                                <TouchableOpacity 
                                    style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
                                    onPress={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    <Text style={styles.paginationButtonText}>Next</Text>
                                </TouchableOpacity>
                            </View>
                        )}
                    </View>
                )}
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#131313',
    },
    content: {
        flex: 1,
        padding: 20,
    },
    searchSection: {
        marginBottom: 30,
    },
    sectionTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: '#FFFFFF',
        marginBottom: 20,
        fontFamily: 'ClimateCrisis',
    },
    controlRow: {
        flexDirection: 'row',
        gap: 15,
        marginBottom: 15,
    },
    controlGroup: {
        flex: 1,
    },
    controlLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: 'rgba(255,255,255,0.8)',
        marginBottom: 8,
    },
    searchQueryContainer: {
        marginBottom: 15,
    },
    searchInput: {
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 8,
        padding: 12,
        color: '#FFFFFF',
        fontSize: 16,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
    },
    advancedToggle: {
        alignSelf: 'flex-start',
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 6,
        marginBottom: 15,
    },
    advancedToggleText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
    },
    advancedPanel: {
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
    },
    advancedTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#FFFFFF',
        marginBottom: 15,
    },
    advancedSection: {
        marginBottom: 20,
    },
    advancedSectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: 'rgba(255,255,255,0.9)',
        marginBottom: 10,
    },
    rangeRow: {
        flexDirection: 'row',
        gap: 20,
    },
    rangeGroup: {
        flex: 1,
    },
    rangeLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: 'rgba(255,255,255,0.8)',
        marginBottom: 8,
    },
    rangeInputs: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    rangeInput: {
        flex: 1,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 6,
        padding: 8,
        color: '#FFFFFF',
        fontSize: 14,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
    },
    rangeSeparator: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 14,
    },
    checkboxRow: {
        flexDirection: 'row',
        gap: 20,
    },
    checkbox: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 6,
    },
    checkboxActive: {
        backgroundColor: 'rgba(139, 92, 246, 0.3)',
        borderWidth: 1,
        borderColor: '#8b5cf6',
    },
    checkboxText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        fontWeight: '500',
    },
    checkboxTextActive: {
        color: '#FFFFFF',
    },
    actionButtons: {
        flexDirection: 'row',
        gap: 15,
        marginTop: 20,
    },
    searchButton: {
        flex: 1,
    },
    clearButton: {
        paddingVertical: 12,
        paddingHorizontal: 20,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    clearButtonText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 16,
        fontWeight: '600',
    },
    resultsSection: {
        marginTop: 20,
    },
    resultsHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 15,
    },
    resultsTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    resultsSubtitle: {
        fontSize: 14,
        color: 'rgba(255,255,255,0.6)',
    },
    resultsTable: {
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        overflow: 'hidden',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: 'rgba(255,255,255,0.1)',
        paddingVertical: 12,
        paddingHorizontal: 8,
    },
    headerCell: {
        flex: 1,
        fontSize: 12,
        fontWeight: '700',
        color: '#FFFFFF',
        textAlign: 'center',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 10,
        paddingHorizontal: 8,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.1)',
    },
    tableRowEven: {
        backgroundColor: 'rgba(255,255,255,0.02)',
    },
    tableCell: {
        flex: 1,
        fontSize: 11,
        color: 'rgba(255,255,255,0.9)',
        textAlign: 'center',
    },
    positiveValue: {
        color: '#4ade80',
        fontWeight: '600',
    },
    negativeValue: {
        color: '#f87171',
    },
    goodBookCount: {
        color: '#4ade80',
        fontWeight: '600',
    },
    lowBookCount: {
        color: '#fbbf24',
        fontWeight: '600',
    },
    pagination: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
        marginTop: 20,
    },
    paginationButton: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 6,
    },
    paginationButtonDisabled: {
        opacity: 0.3,
    },
    paginationButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
    },
    paginationText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
    },
    selectCell: {
        width: 60,
        alignItems: 'center',
        justifyContent: 'center',
    },
    selectCheckbox: {
        width: 24,
        height: 24,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.3)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    selectCheckboxActive: {
        backgroundColor: '#8b5cf6',
        borderColor: '#8b5cf6',
    },
    checkmark: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
    sgpBuilderSection: {
        marginTop: 20,
        alignItems: 'center',
    },
    sgpBuilderButton: {
        minWidth: 250,
    },
    kellyButtonsRow: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    kellyPresetButton: {
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.18)',
        backgroundColor: 'rgba(255,255,255,0.05)',
    },
    kellyPresetButtonActive: {
        backgroundColor: 'rgba(162, 89, 255, 0.4)',
        borderColor: '#A259FF',
    },
    kellyPresetText: {
        color: 'rgba(255,255,255,0.7)',
        fontWeight: '400',
        fontSize: 13,
    },
    kellyPresetTextActive: {
        color: '#FFFFFF',
        fontWeight: '700',
    },
    kellyCustomInput: {
        color: '#FFFFFF',
        fontSize: 13,
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.18)',
        backgroundColor: 'rgba(255,255,255,0.05)',
        minWidth: 60,
    },
    kellyHint: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 11,
        marginTop: 4,
        fontStyle: 'italic',
    },
    activeFiltersSection: {
        marginTop: 16,
        padding: 12,
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.3)',
    },
    activeFiltersLabel: {
        color: '#a78bfa',
        fontSize: 13,
        fontWeight: '600',
        marginBottom: 8,
    },
    activeFiltersChips: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 8,
    },
    activeFilterChip: {
        paddingVertical: 4,
        paddingHorizontal: 8,
        backgroundColor: 'rgba(139, 92, 246, 0.2)',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.4)',
    },
    activeFilterChipText: {
        color: '#c4b5fd',
        fontSize: 11,
        fontWeight: '600',
    },
    activeFiltersHint: {
        color: 'rgba(196, 181, 253, 0.7)',
        fontSize: 10,
        fontStyle: 'italic',
    },
    cacheStatusContainer: {
        marginTop: 16,
        marginBottom: 8,
        padding: 14,
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.3)',
    },
    cacheStatusHighlight: {
        backgroundColor: 'rgba(251, 191, 36, 0.15)',
        borderColor: 'rgba(251, 191, 36, 0.4)',
    },
    cacheStatusText: {
        color: '#c4b5fd',
        fontSize: 13,
        fontWeight: '500',
        textAlign: 'center',
        lineHeight: 18,
    },
    // Sportsbooks Multi-Select Styles
    sportsbooksSelector: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 8,
        padding: 12,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
    },
    sportsbooksSelectorText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        flex: 1,
    },
    sportsbooksArrow: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 12,
        marginLeft: 8,
    },
    sportsbooksDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: '#1a1a1a',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
        marginTop: 4,
        zIndex: 1000,
        maxHeight: 400,
        overflow: 'hidden',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }),
    },
    sportsbooksDropdownHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.1)',
        backgroundColor: 'rgba(255,255,255,0.05)',
    },
    sportsbooksQuickAction: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(139,92,246,0.2)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(139,92,246,0.4)',
    },
    sportsbooksQuickActionText: {
        color: '#a78bfa',
        fontSize: 12,
        fontWeight: '600',
    },
    sportsbooksCloseBtn: {
        marginLeft: 'auto',
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(74,222,128,0.2)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(74,222,128,0.4)',
    },
    sportsbooksCloseBtnText: {
        color: '#4ade80',
        fontSize: 12,
        fontWeight: '600',
    },
    sportsbooksList: {
        padding: 12,
        maxHeight: 320,
        overflow: 'scroll',
    },
    sportsbooksTierLabel: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginTop: 12,
        marginBottom: 8,
    },
    sportsbooksGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    sportsbookChip: {
        paddingVertical: 6,
        paddingHorizontal: 10,
        backgroundColor: 'rgba(255,255,255,0.08)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.15)',
    },
    sportsbookChipSelected: {
        backgroundColor: 'rgba(139,92,246,0.3)',
        borderColor: '#8b5cf6',
    },
    sportsbookChipText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 12,
        fontWeight: '500',
    },
    sportsbookChipTextSelected: {
        color: '#FFFFFF',
        fontWeight: '600',
    },
    // Selected Sportsbooks Summary Section
    selectedSportsbooksSection: {
        marginTop: 12,
        padding: 12,
        backgroundColor: 'rgba(139,92,246,0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(139,92,246,0.3)',
    },
    selectedSportsbooksHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    selectedSportsbooksLabel: {
        color: '#a78bfa',
        fontSize: 13,
        fontWeight: '600',
    },
    clearSportsbooksBtn: {
        color: '#f87171',
        fontSize: 12,
        fontWeight: '600',
    },
    selectedSportsbooksChips: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    selectedSportsbookChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingLeft: 8,
        paddingRight: 6,
        backgroundColor: 'rgba(139,92,246,0.25)',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: 'rgba(139,92,246,0.5)',
    },
    selectedSportsbookChipText: {
        color: '#c4b5fd',
        fontSize: 11,
        fontWeight: '600',
    },
    selectedSportsbookRemove: {
        color: 'rgba(196,181,253,0.7)',
        fontSize: 14,
        fontWeight: '600',
    },
});

export default UnifiedSearchScreen;
